import { useMemo, useCallback } from 'react';
import {
  useAppSelector,
  useComparisonPeriod,
  ComparisonPeriod,
  useMetricFormatter,
  useWeekRange,
  WeekRange
} from 'src/utils/Hooks';
import useGenericAdvancedSearch, { UseGenericAdvancedSearchParameters } from 'src/utils/Hooks/useGenericAdvancedSearch';
import { AdvancedSearchByWeekIdResponse } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/types';
import { calculatePercentChange, getAppName, safeDivide } from 'src/utils/app';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import AdvancedSearchRequestBuilder, {
  useAdvancedSearchRequestBuilder
} from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import {
  getFirstWeekIdOfYear,
  getNextYearWeekId,
  getPreviousWeekId,
  getPreviousYearWeekId,
  timestampToWeekId,
  weekIdToTimestamp,
  yearPartOfWeekId
} from 'src/utils/dateUtils';
import { ValueOf } from 'sl-api-connector';
import _get from 'lodash/get';
import { useAtlasV2ApplyEntityFilters } from 'src/components/AtlasV2Components/Hooks/useAtlasV2BaseMetricRequestBuilder';
import { getLastWeekOfYear } from 'src/utils/dateformatting';

/**
 * Calculates percentage change for promotions using matching week logic.
 * This ensures only weeks with data in both primary and secondary periods are compared.
 *
 * @param primarySlicedData - Primary time period data as [timestamp, value] pairs
 * @param secondarySlicedData - Secondary time period data as [timestamp, value] pairs
 * @param comparisonPeriod - The comparison period type (optional)
 * @param secondaryTotal - Total value for secondary period (optional, used for non-prior-year comparisons)
 * @param mainTimePeriodId - Main time period ID (optional, for last week handling)
 * @returns Calculated percentage change
 */
export const calculatePromotionsPercentChange = (
  primarySlicedData: number[][],
  secondarySlicedData: number[][],
  comparisonPeriod?: string,
  secondaryTotal?: number,
  mainTimePeriodId?: string
): number => {
  // For last week, use the last values directly
  if (mainTimePeriodId === '1w') {
    const lastPrimaryValue = primarySlicedData[primarySlicedData.length - 1];
    const lastSecondaryValue = secondarySlicedData[secondarySlicedData.length - 1];
    return calculatePercentChange(lastPrimaryValue?.[1] || 0, lastSecondaryValue?.[1] || 0);
  }

  // Calculate primary total
  const primaryValueSum = primarySlicedData.reduce((acc, curr) => (acc += curr[1]), 0);

  // Find matching elements - only weeks that exist in both datasets
  const matchingElements = secondarySlicedData.filter((secondaryElement) =>
    primarySlicedData.some((primaryElement) => primaryElement[0] === secondaryElement[0])
  );

  // Calculate secondary total based on comparison type
  const matchingSecondaryTotal = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
    ? matchingElements.reduce((acc, curr) => (acc += curr[1]), 0)
    : secondaryTotal || matchingElements.reduce((acc, curr) => (acc += curr[1]), 0);

  return calculatePercentChange(primaryValueSum, matchingSecondaryTotal);
};

export interface UseMetricByWeekIdArgs {
  indexName: string;
  fieldName: string;
  comparisonIndexName?: string;
  comparisonFieldName?: string;
  /**
   * True if we want to shift the prior year comparison
   * data up to match the current year
   */
  isComparisonDataCustom?: boolean;
  entityType?: string;
  /**
   * Will use most recent week's data instead of full time period
   */
  useLatestWeek?: boolean;
  /**
   * Allows optionally modifying the primary aggregation builder
   * before the request is fired
   */
  primaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
  /**
   * Allows optionally modifying the secondary aggregation builder
   * before the request is fired
   */
  secondaryAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;

  requestBuilder?: (requestBuilder: AdvancedSearchRequestBuilder, comparison: boolean) => AdvancedSearchRequestBuilder;
  /**
   * Sometimes we want to display the metric differently than the default
   */
  metricTypeDisplay?: ValueOf<typeof METRICTYPE>;

  /**
   * Sometimes we want to compute the total value differently than the default
   */
  aggregationFunctionForTotal?: string;

  /**
   * Allows optionally modifying the data set before it is used
   * to compute the total value
   */
  buildDataSetForMetricTotal?: (
    defaultDataSet: number[][],
    indexNameToUse,
    fieldNameToUse,
    isComparison: boolean
  ) => number[][];

  /**
   * Typically we compare YTD data against comparison up until the current week.
   * This allows a custom comparator function if needed.
   */
  customFilterComparisonForTotal?: (row: number[]) => boolean;

  useGenericAdvancedSearchArgs?: Partial<UseGenericAdvancedSearchParameters>;

  /**
   * Some Atlas request should fetch every categories in the market to calculate marketShare.
   * @param requestBuilder
   * @param comparison
   * @returns
   */
  marketShareBuilder?: (
    requestBuilder: AdvancedSearchRequestBuilder,
    comparison: boolean
  ) => AdvancedSearchRequestBuilder;

  /**
   * Some MarketShare, we cant to compare one fieldName to different fieldName. this will be used for finding values (additionalValues) in AdvancedSearch response.
   */
  marketShareKey?: string[];

  /**
   * Promotions etc will return all the available weeks, so we will modifY in the response - getSlicedByWeekIdSplineData
   * !! THIS DOES NOT CHANGE THE REQUESTS itself
   */
  sliceWeekRange?: number[];
}

/**
 * helper function to get a field by index name and field name
 */
const getField = (indexName: string, fieldName: string, entityType?: string) => {
  return INDEX_FIELDS.getField(getAppName(), indexName, fieldName, entityType);
};

/**
 * Get the key from the response
 */
const getMetricKey = (indexName: string, fieldName: string, entityType?: string) => {
  const countKeys = ['dataPointCount'];
  const keyOverride = {
    retailPriceMarketShareV2: {
      fieldName: 'retailPrice',
      aggregationFunction: 'computed',
      keyEnding: 'value'
    },
    retailSalesMarketShareV2: {
      fieldName: 'retailSales',
      aggregationFunction: 'sum',
      keyEnding: 'value'
    },
    unitsSoldMarketShareV2: {
      fieldName: 'unitsSold',
      aggregationFunction: 'sum',
      keyEnding: 'value'
    },
    retailSalesAvg: {
      fieldName: 'retailSales',
      aggregationFunction: 'avg',
      keyEnding: 'value'
    },
    unitsSoldAvg: {
      fieldName: 'unitsSold',
      aggregationFunction: 'avg',
      keyEnding: 'value'
    },
    catalogEfficiency: {
      fieldName: 'unitsSold',
      aggregationFunction: 'avg',
      keyEnding: 'value'
    }
  };
  if (fieldName in keyOverride) {
    const overRideObject = keyOverride[fieldName];
    return `${overRideObject.fieldName}_${overRideObject.aggregationFunction}_${overRideObject.keyEnding}`;
  }

  const field = getField(indexName, fieldName, entityType);
  const keyEnding = field.aggregationFunction === 'stats' ? 'avg' : countKeys.includes(fieldName) ? 'count' : 'value';

  return `${fieldName}_${field.aggregationFunction}_${keyEnding}`;
};

/**
 * Returns data formatted as a 2D array with x and y values
 * (x as a unix timestamp) for displaying in a spline chart
 */
const getSplineData = (indexName: string, fieldName: string, data: any[], entityType?: string): number[][] => {
  return [...data]
    .sort((a, b) => a.weekId - b.weekId)
    .map((row) => [weekIdToTimestamp(Number(row.weekId)), row[getMetricKey(indexName, fieldName, entityType)]]);
};

function generateWeekIds(start, end) {
  const weekIds = [];
  const startYear = Math.floor(start / 100);
  const startWeek = start % 100;
  const endYear = Math.floor(end / 100);
  const endWeek = end % 100;

  for (let year = startYear; year <= endYear; year++) {
    const maxWeek = year === endYear ? endWeek : 52;

    for (let week = year === startYear ? startWeek : 1; week <= maxWeek; week++) {
      if (week <= 52) {
        weekIds.push(year * 100 + week);
      }
    }
  }

  return weekIds;
}

/**
 * For PromotionsData api returns all the promotions, and UI should slice the input
 * */

const getSlicedByWeekIdSplineData = ({
  indexName,
  fieldName,
  data,
  sliceWeekRange,
  entityType,
  comparisonObject
}: {
  indexName: string;
  fieldName: string;
  data: any[];
  sliceWeekRange: number[];
  entityType?: string;
  comparisonObject?: { isComparisonDataCustom: boolean; comparisonPeriod: string } | undefined;
}): number[][] => {
  if (fieldName === 'dataPointCount') {
    const weekIdsInRange = generateWeekIds(sliceWeekRange[0], sliceWeekRange[1]);

    const filledData = weekIdsInRange.map((weekId) => {
      const existing = data.find((d) => d.weekId === String(weekId));
      return (
        existing || {
          weekId: String(weekId),
          dataPointCount_sum_value: 0,
          dataPointCount_sum_count: 0
        }
      );
    });

    return [...filledData]
      .sort((a, b) => a.weekId - b.weekId)
      .filter((datum) => datum.weekId <= sliceWeekRange[1] && datum.weekId >= sliceWeekRange[0])
      .map((row) => {
        return [
          comparisonObject &&
            !comparisonObject.isComparisonDataCustom &&
            comparisonObject.comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
            ? weekIdToTimestamp(getNextYearWeekId(Number(row.weekId)))
            : weekIdToTimestamp(Number(row.weekId)),
          row[getMetricKey(indexName, fieldName, entityType)]
        ];
      });
  }

  return [...data]
    .sort((a, b) => a.weekId - b.weekId)
    .filter((datum) => datum.weekId <= sliceWeekRange[1] && datum.weekId >= sliceWeekRange[0])
    .map((row) => [weekIdToTimestamp(Number(row.weekId)), row[getMetricKey(indexName, fieldName, entityType)]]);
};

/**
 * Gets all the spline chart data and legend data given an index name and field name in order
 * to display a spline chart, this is copied from useMetricByWeekId that was originally created for BeaconPro.
 * However we will need to have marketShareBuilder props, and it will go very large file. so we decided to split the hook.
 * Unless you are not doing marketShare in your product, please use `useMetricByWeekId.ts` from beacon.
 */
export const useAtlasV2MetricByWeekId = <TAdditionalValues>({
  indexName,
  fieldName,
  comparisonFieldName,
  comparisonIndexName,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  metricTypeDisplay,
  aggregationFunctionForTotal,
  requestBuilder,
  useLatestWeek = false,
  isComparisonDataCustom,
  buildDataSetForMetricTotal,
  customFilterComparisonForTotal,
  useGenericAdvancedSearchArgs = {},
  marketShareBuilder,
  marketShareKey
}: UseMetricByWeekIdArgs) => {
  const applyEntityFilters = useAtlasV2ApplyEntityFilters();

  const formatMetric = useMetricFormatter();

  const comparisonPeriod = useComparisonPeriod();
  const weekRange = useWeekRange();

  // Special case, when comparing last week with prior year we want
  // to show the graph for YTD but the legend should just be last week

  const isLastWeekWithPriorYear = useMemo(() => {
    return weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;
  }, [comparisonPeriod, weekRange]);

  const defaultStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);

  const startWeekId = useMemo(() => {
    if (useLatestWeek) {
      return endWeekId;
    }

    return isLastWeekWithPriorYear ? getFirstWeekIdOfYear(yearPartOfWeekId(endWeekId)) : defaultStartWeekId;
  }, [defaultStartWeekId, endWeekId, isLastWeekWithPriorYear, useLatestWeek]);

  const defaultComparisonStartWeekId = useAppSelector((state) => state.comparisonTimePeriod.startWeek);
  const defaultComparisonEndWeekId = useAppSelector((state) => state.comparisonTimePeriod.endWeek);

  const comparisonStartWeekId = useMemo(
    () => (isLastWeekWithPriorYear ? getPreviousYearWeekId(startWeekId) : defaultComparisonStartWeekId),
    [defaultComparisonStartWeekId, isLastWeekWithPriorYear, startWeekId]
  );

  const comparisonEndWeekId = useMemo(
    () =>
      isLastWeekWithPriorYear || (weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR)
        ? getPreviousWeekId(startWeekId)
        : defaultComparisonEndWeekId,
    [comparisonPeriod, defaultComparisonEndWeekId, isLastWeekWithPriorYear, startWeekId, weekRange]
  );

  const entity = useAppSelector((state) => state?.entityService?.mainEntity);
  const requestIdWithEntityInfo = `useMetricByWeekId-${indexName}-${fieldName}-${entity?.type}-${entity?.id}`;

  const retailerId = useAppSelector((state) => state.retailer.id);

  const field = useMemo(
    () => INDEX_FIELDS.getField(getAppName(), indexName, fieldName, entityType),
    [indexName, fieldName, entityType]
  );

  const baseRequestBuilderFromHook = useAdvancedSearchRequestBuilder(
    requestIdWithEntityInfo,
    `${getAppName()}-${indexName}`
  );
  const baseRequestBuilder = useMemo(() => {
    return baseRequestBuilderFromHook
      .setDoAggregation(true)
      .setPageNumber(1)
      .setPageSize(1200)
      .setPeriod('year')
      .setReturnDocuments(false)
      .setSearchBy('parent');
  }, [baseRequestBuilderFromHook]);

  const baseAggregationBuilder = useMemo(() => {
    return new AggregationBuilder('weekId').addConditionTermFilter('retailerId', [retailerId]);
  }, [retailerId]);

  /** ************** Actual and Comparison Request Builders ***************** */
  const mainAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', startWeekId, endWeekId);

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    if (primaryAggregationBuilder) {
      return primaryAggregationBuilder(aggBuilder.clone());
    }

    return aggBuilder;
  }, [baseAggregationBuilder, endWeekId, field, primaryAggregationBuilder, startWeekId]);

  const mainRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(mainAggregationBuilder.build())
      .addConditionRangeFilter('weekId', startWeekId, endWeekId)
      .apply(applyEntityFilters);

    if (requestBuilder) {
      return requestBuilder(body, false).build();
    }

    return body.build();
  }, [baseRequestBuilder, mainAggregationBuilder, startWeekId, endWeekId, applyEntityFilters, requestBuilder]);

  const mainMarketAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', startWeekId, endWeekId);

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    if (primaryAggregationBuilder) {
      return primaryAggregationBuilder(aggBuilder.clone());
    }

    return aggBuilder;
  }, [baseAggregationBuilder, endWeekId, field, primaryAggregationBuilder, startWeekId]);

  const marketShareRequestBody = useMemo(() => {
    if (!marketShareBuilder) {
      return null;
    }
    const body = baseRequestBuilder
      .clone()
      .addAggregation(mainMarketAggregationBuilder.build())
      .addConditionRangeFilter('weekId', startWeekId, endWeekId)
      .apply(applyEntityFilters);

    const marketShareBody = marketShareBuilder(body, false);

    return marketShareBody.build();
  }, [
    marketShareBuilder,
    baseRequestBuilder,
    mainMarketAggregationBuilder,
    startWeekId,
    endWeekId,
    applyEntityFilters
  ]);

  const comparisonAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    if (secondaryAggregationBuilder) {
      return secondaryAggregationBuilder(aggBuilder.clone());
    }

    return aggBuilder;
  }, [baseAggregationBuilder, comparisonEndWeekId, comparisonStartWeekId, field, secondaryAggregationBuilder]);

  const comparisonMarketAggregationBuilder = useMemo(() => {
    const aggBuilder = baseAggregationBuilder
      .clone()
      .addAggregationField(
        field.displayName,
        field.name,
        field.aggregationFunction,
        true,
        field.aggregationFunctionExpression
      )
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    if (field.dependentFields) {
      field.dependentFields.forEach((dependentField) => {
        aggBuilder.addAggregationField(
          dependentField.displayName,
          dependentField.name,
          dependentField.aggregationFunction,
          true
        );
      });
    }

    if (primaryAggregationBuilder) {
      return primaryAggregationBuilder(aggBuilder.clone());
    }

    return aggBuilder;
  }, [
    baseAggregationBuilder,
    comparisonEndWeekId,
    comparisonStartWeekId,
    field.aggregationFunction,
    field.aggregationFunctionExpression,
    field.dependentFields,
    field.displayName,
    field.name,
    primaryAggregationBuilder
  ]);

  const comparisonRequestBody = useMemo(() => {
    const body = baseRequestBuilder
      .clone()
      .addAggregation(comparisonMarketAggregationBuilder.build())
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId)
      .apply(applyEntityFilters);

    if (requestBuilder) {
      return requestBuilder(body, true).build();
    }
    return body.build();
  }, [
    baseRequestBuilder,
    comparisonMarketAggregationBuilder,
    comparisonStartWeekId,
    comparisonEndWeekId,
    applyEntityFilters,
    requestBuilder
  ]);

  const marketShareComparisonRequestBody = useMemo(() => {
    if (!marketShareBuilder) {
      return null;
    }

    const body = baseRequestBuilder
      .clone()
      .addAggregation(comparisonAggregationBuilder.build())
      .addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId)
      .apply(applyEntityFilters);

    const marketShareBody = marketShareBuilder(body, true);

    return marketShareBody.build();
  }, [
    marketShareBuilder,
    baseRequestBuilder,
    comparisonAggregationBuilder,
    comparisonStartWeekId,
    comparisonEndWeekId,
    applyEntityFilters
  ]);

  const { data: response, isLoading } = useGenericAdvancedSearch<AdvancedSearchByWeekIdResponse<TAdditionalValues[]>>({
    requestBody: [
      mainRequestBody,
      comparisonRequestBody,
      ...(marketShareRequestBody ? [marketShareRequestBody, marketShareComparisonRequestBody] : [])
    ],
    requestId: requestIdWithEntityInfo,
    queryKeys: [
      mainRequestBody,
      comparisonRequestBody,
      ...(marketShareRequestBody ? [marketShareRequestBody, marketShareComparisonRequestBody] : [])
    ],
    ...useGenericAdvancedSearchArgs
  });

  const mainRowsWithWeekId: (TAdditionalValues & { weekId: number })[] = useMemo(() => {
    return response && response.data && response.data[0]
      ? response.data[0].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
        weekId: fieldId,
        ...additionalValues
      }))
      : [];
  }, [response]);

  const mainRowsWithMarketShareWithWeekId: (TAdditionalValues & { weekId: number })[] = useMemo(() => {
    if (response && response.data && response.data[0] && response.data[2]) {
      const clientData = response.data[0].aggregations.by_weekId;
      const marketData = response.data[2].aggregations.by_weekId;

      const clientDataMap = new Map();
      clientData.forEach((datum) => {
        clientDataMap.set(datum.fieldId, datum);
      });

      return marketData.map(({ fieldId, additionalValues }) => {
        const metricKey = getMetricKey(indexName, fieldName, entityType);

        const marketValueToUse = marketShareKey
          ? additionalValues[marketShareKey[1]]
          : additionalValues[metricKey] || 0;

        const clientValueToUse = marketShareKey
          ? clientDataMap.get(fieldId)?.additionalValues?.[marketShareKey[0]] || 0
          : clientDataMap.get(fieldId)?.additionalValues?.[metricKey] || 0;

        return {
          weekId: fieldId,
          ...additionalValues,
          [getMetricKey(indexName, fieldName, entityType)]: safeDivide(clientValueToUse, marketValueToUse),
          marketValue: marketValueToUse,
          clientValue: clientValueToUse
        };
      });
    }
    return [];
  }, [entityType, fieldName, indexName, marketShareKey, response]);

  const comparisonRowsWithWeekId: (TAdditionalValues & { weekId: number })[] = useMemo(() => {
    return response && response.data && response.data[1]
      ? response.data[1].aggregations.by_weekId.map(({ fieldId, additionalValues }) => ({
        weekId: fieldId,
        ...additionalValues
      }))
      : [];
  }, [response]);

  const comparisonRowsWithMarketShareWithWeekId: (TAdditionalValues & { weekId: number })[] = useMemo(() => {
    if (response && response.data && response.data[0] && response.data[2]) {
      const clientData = response.data[1].aggregations.by_weekId;
      const marketData = response.data[3].aggregations.by_weekId;

      const clientDataMap = new Map();
      clientData.forEach((datum) => clientDataMap.set(datum.fieldId, datum));

      return marketData.map(({ fieldId, additionalValues }) => {
        const metricKey = getMetricKey(indexName, fieldName, entityType);

        const marketValueToUse = marketShareKey
          ? additionalValues[marketShareKey[1]]
          : additionalValues[metricKey] || 0;

        const clientValueToUse = marketShareKey
          ? clientDataMap.get(fieldId)?.additionalValues[marketShareKey[0]] || 0
          : clientDataMap.get(fieldId)?.additionalValues[metricKey] || 0;

        return {
          weekId: fieldId,
          ...additionalValues,
          [getMetricKey(indexName, fieldName, entityType)]: safeDivide(clientValueToUse, marketValueToUse),
          marketValue: marketValueToUse,
          clientValue: clientValueToUse
        };
      });
    }
    return [];
  }, [entityType, fieldName, indexName, marketShareKey, response]);

  /* *************** Helper functions for computing legend values ************************ */
  /**
   * Get the total computed value for a metric. It will be a sum,
   * average, or computation based on the index field
   */

  const getMetricTotalValue = useCallback(
    (indexNameToUse: string, fieldNameToUse: string, comparison = false, aggFunction?: string) => {
      const fieldToUse = getField(indexNameToUse, fieldNameToUse, entityType);

      // Get the primary data set week ids in order
      // so we can compare against comparison data set
      const mainWeekIds = mainRowsWithWeekId.map((row) => Number(row.weekId)).sort((a, b) => a - b);

      const defaultDataSet = comparison
        ? getSplineData(indexNameToUse, fieldNameToUse, comparisonRowsWithWeekId, entityType).filter((row) => {
          if (customFilterComparisonForTotal) {
            return customFilterComparisonForTotal(row);
          }
          // If we're comparing YTD against prior year, only compare against the same weeks
          if (
            !isComparisonDataCustom &&
            comparisonPeriod === ComparisonPeriod.PRIOR_YEAR &&
            weekRange === WeekRange.YTD
          ) {
            return mainWeekIds.includes(getNextYearWeekId(timestampToWeekId(row[0])));
          }
          return true;
        })
        : getSplineData(indexNameToUse, fieldNameToUse, mainRowsWithWeekId, entityType);

      const dataSet = buildDataSetForMetricTotal
        ? buildDataSetForMetricTotal(defaultDataSet, indexNameToUse, fieldNameToUse, comparison)
        : defaultDataSet;

      const aggregationFunctionToUse = aggFunction || fieldToUse.aggregationFunction;

      // When last week with prior year, we're showing YTD data
      // but the legend should just show the last week value, which
      // will be the last item in the data set. We also show
      // the last value for cardinality metric types
      if ((isLastWeekWithPriorYear || aggregationFunctionToUse === 'cardinality') && dataSet.length > 0) {
        // Get the last week of comparison matching the last week of the main data set
        if (comparison) {
          const lastWeekId = mainWeekIds[mainWeekIds.length - 1];
          const lastComparisonRow = dataSet.find(
            (row) =>
              (isComparisonDataCustom ? timestampToWeekId(row[0]) : getNextYearWeekId(timestampToWeekId(row[0]))) ===
              lastWeekId
          );
          return lastComparisonRow ? lastComparisonRow[1] : 0;
        }
        return dataSet[dataSet.length - 1][1];
      }

      const total = dataSet.reduce((acc, row) => acc + row[1], 0);

      if (aggregationFunctionToUse === 'avg' || aggregationFunctionToUse === 'stats') {
        return dataSet.length > 0 ? total / dataSet.length : 0;
      }

      // Computed index fields have an function evaluator that take
      // variables to compute the total. We should already have the total
      // values for the variables it relies on, so get the values
      // and use them to compute the total
      if (aggregationFunctionToUse === 'computed') {
        const dependencies = fieldToUse.dependentFields;
        const variableValues = dependencies.reduce((acc, depField) => {
          return {
            ...acc,
            [depField.nameAlias || depField.name]: getMetricTotalValue(depField.indexName, depField.name, comparison)
          };
        }, {} as Record<string, number>);
        return fieldToUse.aggregationFunctionEvaluator.evaluate(variableValues);
      }

      return total;
    },
    [
      buildDataSetForMetricTotal,
      comparisonPeriod,
      comparisonRowsWithWeekId,
      customFilterComparisonForTotal,
      entityType,
      isComparisonDataCustom,
      isLastWeekWithPriorYear,
      mainRowsWithWeekId,
      weekRange
    ]
  );

  const getMarketShareTotalValue = useCallback(
    (comparison = false) => {
      const mainWeekIds = mainRowsWithMarketShareWithWeekId.map((row) => +row.weekId);

      const shouldUseYTDCompare = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR && weekRange === WeekRange.YTD;

      const rowsWithMarketShareWithWeekIdToUse = !comparison
        ? mainRowsWithMarketShareWithWeekId
        : shouldUseYTDCompare
          ? comparisonRowsWithMarketShareWithWeekId.filter((row) => mainWeekIds.includes(getNextYearWeekId(row.weekId)))
          : comparisonRowsWithMarketShareWithWeekId;

      const totalMarketValue = rowsWithMarketShareWithWeekIdToUse.reduce((acc, curr) => acc + curr.marketValue, 0);
      const totalClientValue = rowsWithMarketShareWithWeekIdToUse.reduce((acc, curr) => acc + curr.clientValue, 0);

      return safeDivide(totalClientValue, totalMarketValue);
    },
    [comparisonPeriod, comparisonRowsWithMarketShareWithWeekId, mainRowsWithMarketShareWithWeekId, weekRange]
  );

  const lastSecondaryValue = useMemo(() => {
    const compareWeekIdsRowToUse = marketShareBuilder
      ? comparisonRowsWithMarketShareWithWeekId
      : comparisonRowsWithWeekId;
    const lastWeekBucket = compareWeekIdsRowToUse.find(
      (w) => w.weekId.toString() === defaultComparisonEndWeekId.toString()
    );

    if (lastWeekBucket) {
      return lastWeekBucket[getMetricKey(indexName, fieldName, entityType)];
    }

    return null;
  }, [
    comparisonRowsWithMarketShareWithWeekId,
    comparisonRowsWithWeekId,
    defaultComparisonEndWeekId,
    entityType,
    fieldName,
    indexName,
    marketShareBuilder
  ]);

  return useMemo(() => {
    const compareWeekIdsRowToUse = marketShareBuilder
      ? comparisonRowsWithMarketShareWithWeekId
      : comparisonRowsWithWeekId;

    const secondaryData = getSplineData(
      comparisonIndexName || indexName,
      comparisonFieldName || fieldName,
      compareWeekIdsRowToUse.map((row) => ({
        ...row,
        weekId:
          !isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
            ? getNextYearWeekId(row.weekId)
            : row.weekId
      })),
      entityType
    );

    const secondarySlicedData = getSlicedByWeekIdSplineData({
      indexName: comparisonIndexName || indexName,
      fieldName: comparisonFieldName || fieldName,
      entityType,
      comparisonObject: { isComparisonDataCustom, comparisonPeriod },
      sliceWeekRange:
        !isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
          ? [
            comparisonStartWeekId,
            weekRange === WeekRange.YTD ? getLastWeekOfYear(comparisonEndWeekId) : comparisonEndWeekId
          ]
          : [
            comparisonStartWeekId,
            weekRange === WeekRange.YTD ? getLastWeekOfYear(comparisonEndWeekId) : comparisonEndWeekId
          ],
      data: compareWeekIdsRowToUse
    });

    const basePrimaryData = marketShareBuilder
      ? getSplineData(indexName, fieldName, mainRowsWithMarketShareWithWeekId, entityType)
      : getSplineData(indexName, fieldName, mainRowsWithWeekId, entityType);

    const basePrimarySlicedData = marketShareBuilder
      ? getSlicedByWeekIdSplineData({
        indexName,
        fieldName,
        data: mainRowsWithMarketShareWithWeekId,
        sliceWeekRange: [startWeekId, endWeekId],
        entityType
      })
      : getSlicedByWeekIdSplineData({
        indexName,
        fieldName,
        data: mainRowsWithWeekId,
        sliceWeekRange: [startWeekId, endWeekId],
        entityType
      });

    // Get last data point
    const lastPrimaryValue = _get(basePrimaryData[basePrimaryData.length - 1], ['1']) ?? 0;

    // The first element is the epoch timestamp and the 2nd is the value

    const percentChangeLastValues = calculatePercentChange(lastPrimaryValue, lastSecondaryValue);

    let percentChange: number;

    if (marketShareBuilder) {
      percentChange = calculatePercentChange(getMarketShareTotalValue(false), getMarketShareTotalValue(true));
    } else if (indexName === 'promotions' && fieldName === 'dataPointCount') {
      percentChange = calculatePromotionsPercentChange(basePrimarySlicedData, secondarySlicedData, comparisonPeriod);
    } else {
      percentChange = calculatePercentChange(
        getMetricTotalValue(indexName, fieldName, false, aggregationFunctionForTotal),
        getMetricTotalValue(indexName, fieldName, true, aggregationFunctionForTotal)
      );
    }

    const metricType = metricTypeDisplay || field.metricType;
    const comparisonField = INDEX_FIELDS.getField(
      getAppName(),
      comparisonIndexName || indexName,
      comparisonFieldName || fieldName,
      entityType
    );
    const comparisonMetricType = metricTypeDisplay || comparisonField.metricType;

    return {
      primaryData: [
        ...(!isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && secondaryData.length > 0
          ? [secondaryData[secondaryData.length - 1]]
          : []),
        ...basePrimaryData
      ],
      primarySlicedData: [
        ...(!isComparisonDataCustom && comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && secondaryData.length > 0
          ? [secondarySlicedData[secondarySlicedData.length - 1]]
          : []),
        ...basePrimarySlicedData
      ],
      mainRowsWithWeekId,
      comparisonRowsWithWeekId,
      secondaryData,
      secondarySlicedData,
      lastPrimaryValue,
      lastSecondaryValue,
      primaryTotal: marketShareBuilder
        ? getMarketShareTotalValue(false)
        : getMetricTotalValue(indexName, fieldName, false, aggregationFunctionForTotal),
      secondaryTotal: marketShareBuilder
        ? getMarketShareTotalValue(true)
        : getMetricTotalValue(
          comparisonIndexName || indexName,
          comparisonFieldName || fieldName,
          true,
          aggregationFunctionForTotal
        ),
      formattedPrimaryTotal: `${formatMetric(
        marketShareBuilder
          ? getMarketShareTotalValue(false)
          : getMetricTotalValue(indexName, fieldName, false, aggregationFunctionForTotal),
        metricTypeDisplay || metricType,
        {
          showFullValue: metricType === METRICTYPE.PERCENT,
          decimalPlaces: 2,
          showNegative: true
        }
      )}${metricTypeDisplay === METRICTYPE.MULTIPLE ? 'x' : ''}`,
      formattedLastPrimaryValue: `${formatMetric(lastPrimaryValue, metricType, {
        showFullValue: field.metricType === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      })}`,
      formattedLastSecondaryValue: `${formatMetric(lastSecondaryValue, comparisonMetricType, {
        showFullValue: comparisonField.metricType === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      })}`,
      percentChangeLastValues,
      formattedPercentChangeLastValues: `${formatMetric(Math.abs(percentChangeLastValues), METRICTYPE.PERCENT, {
        decimalPlaces: 2
      })}`,
      formattedSecondaryTotal: `${formatMetric(
        marketShareBuilder
          ? getMarketShareTotalValue(true)
          : getMetricTotalValue(
            comparisonIndexName || indexName,
            comparisonFieldName || fieldName,
            true,
            aggregationFunctionForTotal
          ),
        comparisonMetricType,
        {
          showFullValue: comparisonMetricType === METRICTYPE.PERCENT,
          decimalPlaces: 2,
          showNegative: true
        }
      )}${comparisonMetricType === METRICTYPE.MULTIPLE ? 'x' : ''}`,
      response,
      percentChange,
      formattedPercentChange: `${formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })}`,
      isLoading,
      field
    };
  }, [
    marketShareBuilder,
    comparisonRowsWithMarketShareWithWeekId,
    comparisonRowsWithWeekId,
    comparisonIndexName,
    indexName,
    comparisonFieldName,
    fieldName,
    entityType,
    isComparisonDataCustom,
    comparisonPeriod,
    comparisonStartWeekId,
    weekRange,
    comparisonEndWeekId,
    mainRowsWithMarketShareWithWeekId,
    mainRowsWithWeekId,
    startWeekId,
    endWeekId,
    lastSecondaryValue,
    getMarketShareTotalValue,
    getMetricTotalValue,
    aggregationFunctionForTotal,
    metricTypeDisplay,
    field,
    formatMetric,
    response,
    isLoading
  ]);
};
